import { action } from '@ember/object';
import { scheduleTask } from 'ember-lifeline';
import { service } from '@ember/service';
import dateSort from 'scaleflow/utils/dateSort';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import ConversationValidator from 'scaleflow/validators/conversation';
export default class ChatComponent extends Component {
  @service currentUser;
  @service dataSocket;
  @service router;
  @service intl;
  @service store;
  @service chatv2;

  @tracked userIsScrolledUp = false;
  @tracked formErrors = [];

  conversationValidator = ConversationValidator;

  get messages() {
    return this.activeConversation?.sortedMessages ?? [];
  }

  get recentChatsIsVisible() {
    return this.chatv2.recentChatsIsVisible;
  }

  @action
  toggleRecentChatsIsVisible() {
    this.chatv2.toggleRecentChatsIsVisible();
  }

  @action
  toggleVisibility() {
    this.chatv2.toggleChatWindow();
  }

  get isVisible() {
    return this.chatv2.chatWindowIsOpen;
  }

  get activeConversation() {
    return this.chatv2.activeConversationRecord ?? {};
  }

  get title() {
    return this.activeConversation?.title ?? '';
  }

  get initiative() {
    return this.activeConversation?.initiative ?? {};
  }

  get messagesAreEmpty() {
    return (this.activeConversation?.messages?.length ?? 0) === 0;
  }

  @action
  emitGapAnalysisRequest() {
    const message = `Please generate a gap analysis report on the ${this.intl.t('_initiatives.title', { num: 1 })}: ${this.activeConversation.title}`;
    this.sendQuery(message);
  }

  @action
  emitSynchronizeDocumentsRequest() {
    const message = `Please synchronize the documents in our knowledge basse for the ${this.intl.t('_initiatives.title', { num: 1 })}: ${this.activeConversation.title} (ID: ${this.activeConversation.id}).`;
    this.sendQuery(message);
  }

  @action
  emitResearchProjectRequest() {
    const message = `Please do research on the ${this.intl.t('_initiatives.title', { num: 1 })}: ${this.activeConversation.title} (ID: ${this.activeConversation.id}).`;
    this.sendQuery(message);
  }

  @action
  handleAgentInitiate(agent) {
    this.chatv2.handleConversationInitiateWithAgent(agent);
  }

  @action
  sendQuery(query) {
    const activeConversation = this.activeConversation;
    const errors = this.chatv2.validateConversation(activeConversation);
    if (errors && errors.length !== 0) {
      this.formErrors = errors;
      return { success: false, errors: this.formErrors };
    } else {
      this.formErrors = [];
    }
    const conversation_id = activeConversation.id;

    this.chatv2.sendMessage(query, conversation_id);
    return { success: true, errors: undefined };
  }

  // Helper method to scroll to bottom, now called from a single place
  @action
  scrollToBottom() {
    // Only scroll if the user hasn't scrolled up
    if (!this.userIsScrolledUp) {
      scheduleTask(this, 'actions', function () {
        const element = document.querySelector('#messages-container');
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      });
    }
  }

  @action
  setUserScrolledUp(value) {
    this.userIsScrolledUp = value;
  }

  get activeConversations() {
    let conversations = [];
    if (this.chatv2.isInitiativePage) {
      conversations =
        this.chatv2.currentInitiative?.conversations?.content ?? [];
    } else {
      conversations = this.store.peekAll('conversation');
    }

    conversations = conversations
      .filter((conversation) => !conversation.archived)
      .toSorted((a, b) => dateSort(a.created_at, b.created_at, 'desc'));
    return conversations;
  }

  get archivedConversations() {
    return (this.chatv2.currentInitiative?.sortedConversations ?? []).filter(
      (conversation) => conversation.archived,
    );
  }

  get showChatHeader() {
    return this.chatv2.activeConversationRecord?.initiative?.id !== undefined;
  }

  @action
  async activateConversation(conversation) {
    this.chatv2.restoreConversation(conversation);
  }

  @action
  handleRenameConversation() {}

  @action
  deleteConversation(conversation) {
    conversation.destroyRecord();
  }

  @action
  archiveConversation(conversation) {
    conversation.archived = true;
    conversation.save();
  }

  @action
  restoreConversation(conversation) {
    conversation.archived = false;
    conversation.save();
  }

  get agents() {
    return this.store.peekAll('agent');
  }

  @action
  setInitiativeOnConversation(initiative) {
    const conversation = this.activeConversation;
    if (conversation) {
      conversation.initiative = initiative;
      conversation.save();
    }
  }

  @action
  stopConversation() {
    this.chatv2.stopConversation();
  }
}
